import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { ChevronDown, Volume2, VolumeX } from 'lucide-react';

const TEXT_COLLAPSE_LENGTH = 180;

interface PostContentProps {
  content: string;
  formattedContent?: React.ReactNode;
  media?: string;
  video?: string;
  endorsements?: string[];
  isSocialFeed?: boolean;
  className?: string;
  isGridView?: boolean;
}

const ProgressiveImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="relative aspect-square w-full overflow-hidden bg-gray-200 dark:bg-gray-700 sm:rounded-xl">
      {!isLoaded && (
        <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700 z-0" />
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setIsLoaded(true)}
        onError={() => setIsLoaded(true)}
        className={cn(
          'w-full h-full object-cover object-center transition-opacity duration-500 z-10',
          isLoaded ? 'opacity-100' : 'opacity-0'
        )}
        loading="lazy"
      />
    </div>
  );
};

const PostContent: React.FC<PostContentProps> = ({
  content,
  formattedContent,
  media,
  video,
  endorsements,
  isSocialFeed,
  isGridView,
  className
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVertical, setIsVertical] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [posterUrl, setPosterUrl] = useState<string | undefined>(media);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const shouldCollapse = useMemo(() => content.length > TEXT_COLLAPSE_LENGTH, [content]);
  const displayContent = useMemo(() => {
    if (!shouldCollapse || isExpanded) return formattedContent || content;
    return content.slice(0, TEXT_COLLAPSE_LENGTH) + '...';
  }, [content, formattedContent, shouldCollapse, isExpanded]);

  useEffect(() => {
    if (!video || !videoRef.current || !videoContainerRef.current) return;
    const videoEl = videoRef.current;
    const containerEl = videoContainerRef.current;

    const handleMetadataLoaded = () => {
      if (!videoEl) return;
      setIsVertical(videoEl.videoHeight > videoEl.videoWidth);
      setIsLoading(false);
    };

    const handleWaiting = () => setIsLoading(true);
    const handlePlaying = () => setIsLoading(false);

    videoEl.addEventListener('loadedmetadata', handleMetadataLoaded);
    videoEl.addEventListener('waiting', handleWaiting);
    videoEl.addEventListener('playing', handlePlaying);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Try to play the video when it comes into view
            videoEl.play().catch((err) => {
              console.warn('Autoplay prevented:', err);
              // If autoplay is prevented, we can try again with user interaction
              const attemptPlayOnInteraction = () => {
                videoEl.play().catch(e => console.warn('Still cannot play:', e));
                document.removeEventListener('click', attemptPlayOnInteraction);
              };
              document.addEventListener('click', attemptPlayOnInteraction, { once: true });
            });
          } else {
            videoEl.pause();
            videoEl.currentTime = 0;
          }
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.6 }
    );

    observer.observe(containerEl);

    return () => {
      observer.unobserve(containerEl);
      observer.disconnect();
      videoEl.removeEventListener('loadedmetadata', handleMetadataLoaded);
      videoEl.removeEventListener('waiting', handleWaiting);
      videoEl.removeEventListener('playing', handlePlaying);
    };
  }, [video]);

  useEffect(() => {
    if (media || !videoRef.current) return;
    const videoEl = videoRef.current;
    const handleLoadedData = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoEl.videoWidth;
        canvas.height = videoEl.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
          setPosterUrl(canvas.toDataURL('image/png'));
        }
      } catch (e) {
        console.error('Error capturing poster frame', e);
      }
    };

    videoEl.addEventListener('loadeddata', handleLoadedData);
    return () => {
      videoEl.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [media]);

  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    videoRef.current.muted = newMuted;
  };

  return (
    <div className={cn("space-y-4", className)}>
      {(media || video) && (
        <motion.div
          ref={videoContainerRef}
          className={cn(
            "relative overflow-hidden group",
            isGridView
              ? "w-full h-full aspect-square" 
              : "w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] sm:rounded-none"
          )}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          {video ? (
            <div className="relative flex items-center justify-center aspect-square bg-black overflow-hidden sm:rounded-xl">
              {isLoading && (
                <div className="absolute inset-0 bg-gray-200 dark:bg-gray-800 animate-pulse z-0" />
              )}

              <video
                ref={videoRef}
                src={video}
                poster={posterUrl}
                autoPlay
                playsInline
                crossOrigin="anonymous"
                {...{ 'webkit-playsinline': 'true' }}
                className={cn(
                  "w-full h-full object-cover",
                  isVertical
                    ? isGridView 
                      ? "object-cover object-center scale-[1.5] origin-center" 
                      : "object-cover object-center scale-[1.5] sm:scale-[1.2] origin-center"
                    : "object-cover",
                  "transition-all duration-300 cursor-pointer",
                  !isGridView && "sm:max-h-[600px]"
                )}
                onClick={handleVideoClick}
                muted={isMuted}
                loop
                preload="metadata"
              >
                <source src={video} type="video/mp4" />
                <source src={video} type="video/webm" />
                Your browser does not support the video tag.
              </video>

              <div className="absolute bottom-4 right-4">
                <button
                  onClick={toggleMute}
                  className="p-1 rounded-lg bg-dark-900/60 hover:bg-dark-900/80 text-white backdrop-blur-sm transform hover:scale-105 active:scale-95 shadow-lg"
                >
                  {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ) : (
            <div className={isGridView ? "w-full h-full aspect-square" : ""}>
              <ProgressiveImage src={media!} alt="Post image" />
            </div>
          )}
        </motion.div>
      )}

      {(!video || isSocialFeed) && (
        <div className={cn("relative", (!media && !video) || isSocialFeed ? "px-4" : "px-0")}>
          <div className="space-y-2">
            <p className="text-white text-sm sm:text-base leading-relaxed">{displayContent}</p>
            {shouldCollapse && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center space-x-1 text-dark-800 hover:text-gold-400 transition-colors text-sm"
              >
                <span>{isExpanded ? "Show less" : "Read more"}</span>
                <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isExpanded && "rotate-180")} />
              </button>
            )}
          </div>
        </div>
      )}

      {endorsements?.length > 0 && (
        <div className={cn(
          "flex flex-wrap gap-2 mt-3 bg-dark-300 rounded-xl p-4",
          (!media && !video) || isSocialFeed ? "mx-4 sm:mx-4" : "mx-4 sm:mx-0"
        )}>
          {endorsements.map((endorsement, index) => (
            <span key={index} className="px-3 py-1.5 bg-dark-400 rounded-full text-sm text-white">
              {endorsement}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PostContent;